import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface NotificationPreferences {
  id: string;
  user_id: string;
  global_enabled: boolean | null;
  referent_validation_enabled: boolean | null;
  candidate_refusal_enabled: boolean | null;
  campaign_completed_enabled: boolean | null;
  report_generated_enabled: boolean | null;
  fraud_alert_enabled: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface NotificationPreferenceUpdate {
  global_enabled?: boolean | null;
  referent_validation_enabled?: boolean | null;
  candidate_refusal_enabled?: boolean | null;
  campaign_completed_enabled?: boolean | null;
  report_generated_enabled?: boolean | null;
  fraud_alert_enabled?: boolean | null;
}

export function useNotificationPreferences() {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Charge les préférences de notification de l'utilisateur connecté
   */
  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer l'utilisateur connecté
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error("Utilisateur non connecté");
      }

      // Récupérer les préférences
      const { data, error: prefsError } = await supabase
        .from('email_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (prefsError) {
        // Si les préférences n'existent pas, les créer avec les valeurs par défaut
        if (prefsError.code === 'PGRST116') {
          console.log("Préférences non trouvées, création avec valeurs par défaut...");
          
          const { data: newPrefs, error: insertError } = await supabase
            .from('email_notification_preferences')
            .insert({
              user_id: user.id,
              global_enabled: true,
              referent_validation_enabled: true,
              candidate_refusal_enabled: true,
              campaign_completed_enabled: true,
              report_generated_enabled: true,
              fraud_alert_enabled: true
            })
            .select()
            .single();

          if (insertError) {
            throw insertError;
          }

          setPreferences(newPrefs);
          console.log("✅ Préférences créées avec succès");
        } else {
          throw prefsError;
        }
      } else {
        setPreferences(data);
        console.log("✅ Préférences chargées avec succès");
      }
    } catch (err: any) {
      console.error("Erreur lors du chargement des préférences:", err);
      setError(err.message || "Erreur lors du chargement des préférences");
      toast({
        title: "Erreur",
        description: "Impossible de charger vos préférences de notification",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sauvegarde les préférences de notification
   */
  const savePreferences = async (updates: NotificationPreferenceUpdate) => {
    try {
      setSaving(true);
      setError(null);

      // Récupérer l'utilisateur connecté
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error("Utilisateur non connecté");
      }

      // Mettre à jour les préférences
      const { data, error: updateError } = await supabase
        .from('email_notification_preferences')
        .upsert({
          user_id: user.id,
          ...updates,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      setPreferences(data);
      console.log("✅ Préférences sauvegardées avec succès");

      toast({
        title: "Succès",
        description: "Vos préférences de notification ont été sauvegardées",
      });

      return true;
    } catch (err: any) {
      console.error("Erreur lors de la sauvegarde des préférences:", err);
      setError(err.message || "Erreur lors de la sauvegarde des préférences");
      
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder vos préférences",
        variant: "destructive",
      });

      return false;
    } finally {
      setSaving(false);
    }
  };

  // Charger les préférences au montage du composant
  useEffect(() => {
    loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    preferences,
    loading,
    saving,
    error,
    savePreferences,
    refresh: loadPreferences,
  };
}
